import { useClientLogger } from '@landsbankinn/client-logger';
import { useNavigate } from 'react-router';
import {
  ElectronicIdService,
  ElectronicIdServiceToken,
  type IElectronicIdService,
} from 'src/services/ElectronicIdService';
import { ServicesProvider } from 'src/services/ServicesProvider';
import {
  WindowLocationServiceToken,
  type IWindowLocationService,
} from 'src/services/WindowLocationService';
import {
  AllowedLoginType,
  useApplicationServerContext,
  type LoginServerState,
} from '../../../../state';
import { setPreviousLoginType, submit as submitForm } from '../../utils';
import { type NextStepResponseType, type StartResponseType } from '../types';

const LOGIN_PKISIM_ROUTE = '/login/pkisim';

type PkiSimSubmitProps = {
  onCode: (code: string) => void;
  onSubmitting: (isSubmitting: boolean) => void;
  onError: (error: string) => void;
  onXsrf: (xsrfToken: string) => void;
};
export function usePkiSimSubmit({
  onCode,
  onSubmitting,
  onError,
  onXsrf,
}: PkiSimSubmitProps) {
  const [serverState] = useApplicationServerContext() as [LoginServerState];

  const navigate = useNavigate();
  const { logger } = useClientLogger();

  /**
   * Intermediate function to shim Android URL_SCHEME error after refactoring PKISIM flow.
   * Should be removed when https://github.com/landsbankinn/mobilebank-personal/pull/4060 has
   * rolled out to all users and older versions are not supported.
   */
  async function submitUsingFormPost(
    phonenumber: string,
    xsrf: string,
    traceparent: string,
  ) {
    onSubmitting(true);
    setPreviousLoginType(AllowedLoginType.PkiSim);

    const electronicIdService =
      ServicesProvider.getSingleton<IElectronicIdService>(
        ElectronicIdServiceToken,
      );

    const { response, data, error } =
      await electronicIdService.fetchPkiSimResponse<StartResponseType>({
        phonenumber,
        url: '/ui/login/pkisim/start',
        xsrf,
        traceparent,
      });

    if (response.status === 400 && error && error.error_message) {
      onSubmitting(false);
      onError(error.error_message[0]);
    } else if (data) {
      onCode(data.code);
      onXsrf(data.xsrfToken);
    }

    const form = document.querySelector('form');
    if (form !== null) {
      setPreviousLoginType(AllowedLoginType.PkiSim);
      submitForm(form);
    }
  }

  async function submitUsingFetch(
    phonenumber: string,
    xsrf: string,
    traceparent: string,
  ) {
    // Indicate two step login is started
    onSubmitting(true);
    const windowLocationService =
      ServicesProvider.getSingleton<IWindowLocationService>(
        WindowLocationServiceToken,
        navigate,
      );
    const electronicIdService =
      ServicesProvider.getSingleton<ElectronicIdService>(
        ElectronicIdServiceToken,
      );

    const { response, data, error } =
      await electronicIdService.fetchPkiSimResponse<NextStepResponseType>({
        phonenumber,
        url: '/ui/login/pkisim',
        xsrf,
        traceparent,
      });

    setPreviousLoginType(AllowedLoginType.PkiSim);
    if (response.status === 400 && error && error.error_message) {
      logger.warn('Bad request during PKISIM', { ...error });
      onError(error.error_message[0]);
      onSubmitting(false);
    } else if (data) {
      logger.trace('Response from Electronic Id Service', { data });
      if (data.nextStep.includes(LOGIN_PKISIM_ROUTE)) {
        onXsrf(data.xsrfToken);
        logger.message('PKISIM authentication started', { ...data });
        navigate(LOGIN_PKISIM_ROUTE);
      } else {
        logger.warn('Unsupported nextStep in PKISIM response', { ...data });
        windowLocationService.goto(data.nextStep, navigate);
      }
    }
  }

  async function submit(
    phonenumber: string,
    method: 'POST' | 'XMLHttpRequest',
    xsrf?: string,
  ) {
    const xsrfToken: string =
      xsrf || serverState.forms.pkiSim.fields.xsrf.value || '';
    const traceparentValue: string = serverState.traceparent;

    if (method === 'POST') {
      return submitUsingFormPost(phonenumber, xsrfToken, traceparentValue);
    } else {
      return submitUsingFetch(phonenumber, xsrfToken, traceparentValue);
    }
  }
  return submit;
}
